import { parseISO } from 'date-fns';
import { isValidPhoneNumber, parsePhoneNumberWithError, PhoneNumber } from 'libphonenumber-js';
import * as yup from 'yup';

import { Address, requiredAddressSchema } from './address';

export type PersonalInfo = {
  id?: number;
  firstName: string;
  lastName: string;
  address: Address;
  dob: string;
  ssnLast4: string;
  controlPerson: boolean;
  beneficialOwner: boolean;
  authorizedRep: boolean;
};

export type OtherIndividualInfo = {
  uiId: string;
  ssnLast4: string;
  phone: string;
} & Omit<PersonalInfo, 'authorizedRep'>;

const ssnFormat = {
  isNumericString: true,
  allowLeadingZeros: true,
  decimalScale: 0,
  allowNegative: false,
};

export type PreliminaryInfo = {
  email: string;
  phone: string;
};

const firstNameSchema = yup.string().max(40).required('First name is required');
const lastNameSchema = yup.string().max(40).required('Last name is required');
const dobSchema = yup
  .string()
  .nullable()
  .required('Date of birth is required')
  .matches(/^\d{4}-[01]\d-[0-3]\d$/, 'Date of birth is invalid')
  .test('valid-date-of-birth', 'Date of birth is invalid', (dobIsoString) => {
    if (!dobIsoString) {
      return true;
    }

    const date = parseISO(dobIsoString);
    return date.getFullYear() >= 1900 && date < new Date();
  });

const validPhoneNumberCountries = new Set<string>(['US', 'PR']);

const makePhoneSchema = (requiredMessage: string, validMessage: string) =>
  yup
    .string()
    .required(requiredMessage)
    .test('validate-phone', validMessage, (t) => {
      if (!t || !isValidPhoneNumber(t, 'US')) return false;
      const asPhoneNumber: PhoneNumber = parsePhoneNumberWithError(t, 'US');
      return validPhoneNumberCountries.has(asPhoneNumber.country || '');
    });

const makeBeneficialOwnerOrControlPerson = (message: string) =>
  yup
    .boolean()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
    .test('oneOf', message, (value, context) => context.parent.beneficialOwner || context.parent.controlPerson);

const personOwnerOrControlSchema = makeBeneficialOwnerOrControlPerson(
  'You must either be an owner or have management responsibility to proceed',
);

const ssnLast4Schema = yup
  .string()
  .required('Last 4 digits of SSN are required')
  .notOneOf(['0000'], 'Enter a valid last 4 digits of SSN')
  .matches(/^\d{4}$/, 'Enter a valid last 4 digits of SSN');

const personalInfoSchema: Partial<{
  [key in keyof PersonalInfo]: yup.AnySchema;
}> = {
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  address: yup.object(requiredAddressSchema),
  dob: dobSchema,
  ssnLast4: ssnLast4Schema,
  authorizedRep: yup.boolean().required().oneOf([true], 'You must be an authorized representative to proceed'),
  beneficialOwner: personOwnerOrControlSchema,
  controlPerson: personOwnerOrControlSchema,
};

const otherOwnerOrControlSchema = makeBeneficialOwnerOrControlPerson(
  'Must be an owner or have management responsibility',
);

const phoneSchema = makePhoneSchema('Phone is required', 'Enter a valid phone number');
const mobilePhoneSchema = makePhoneSchema('Mobile is required', 'Enter a valid US mobile phone number');

const otherIndividualInfoSchema: Partial<{
  [key in keyof OtherIndividualInfo]: yup.AnySchema;
}> = {
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  dob: dobSchema,
  address: yup.object(requiredAddressSchema),
  phone: phoneSchema,
  ssnLast4: ssnLast4Schema,
  beneficialOwner: otherOwnerOrControlSchema,
  controlPerson: otherOwnerOrControlSchema,
};

const preliminaryInfoSchema: Partial<{
  [key in keyof PreliminaryInfo]: yup.AnySchema;
}> = {
  email: yup.string().email('Enter a valid email').max(320).required('Email is required'),
  phone: mobilePhoneSchema,
};

const initialOtherIndividualInfo: Omit<OtherIndividualInfo, 'uiId'> = {
  firstName: '',
  lastName: '',
  address: {
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
  },
  dob: '',
  controlPerson: false,
  beneficialOwner: false,
  ssnLast4: '',
  phone: '',
};

export {
  preliminaryInfoSchema,
  personalInfoSchema,
  ssnFormat,
  otherIndividualInfoSchema,
  initialOtherIndividualInfo,
  phoneSchema,
};
